import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";

import { exitMultiTeeTimeModeAtom } from "@/features/TeeSheet/atoms";
import { trackBookingConfirmationConversion } from "@/utils/Analytics/Conversion";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import {
  buildBookingConfirmationAnalyticsData,
  readBookingConfirmationAnalyticsData,
} from "@/utils/Analytics/bookings/buildBookingConfirmationAnalyticsData";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { useMutationWithError } from "../../../../../../utils/hooks/useMutationWithError";
import { bookingSummaryAnalyticsDataAtom } from "../atoms/analytics/bookingSummaryAnalyticsDataAtom";
import { getAppliedPromoCodeAnalyticsData } from "../components/BookingCredits/components/BookingPromoCode/promoCodeAnalytics";
import {
  ConfirmDraftBookingsInput,
  useConfirmDraftBookingsMutation,
} from "./__generated__/useConfirmDraftBookingsMutation.graphql";

export const useConfirmDraftBookings = (
  membershipTypeId: string,
  bookingType: "STANDARD" | "LESSON",
  bookingId: string,
  isRequestBooking = false
) => {
  const [confirmDraftBookings, isConfirming] =
    useMutationWithError<useConfirmDraftBookingsMutation>(
      useConfirmDraftBookingsMutationString
    );

  const trackEvent = useTrackEvent();

  const { showToast } = useToast();

  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const exitMultiTeeTimeMode = useSetAtom(exitMultiTeeTimeModeAtom);

  const handleConfirmDraftBookings = (
    input: ConfirmDraftBookingsInput,
    successCallback?: () => void
  ) =>
    confirmDraftBookings({
      variables: {
        input,
        membershipTypeId,
        bookingType,
      },
      onCompleted: (res) => {
        const confirmedBookings =
          res.responsePayload?.bookings
            ?.map(readBookingConfirmationAnalyticsData)
            .filter(
              (booking): booking is NonNullable<typeof booking> => !!booking
            ) ?? [];
        trackBookingConfirmationConversion({
          success: res.responsePayload?.success,
          eventName: "multi_booking_confirmed",
          analyticsData,
          bookings: confirmedBookings,
        });
        trackEvent(
          "BookingDetail.DraftBookingsConfirmed",
          buildBookingConfirmationAnalyticsData(
            analyticsData,
            confirmedBookings
          ) ?? {}
        );
        res.responsePayload?.bookings?.forEach((booking) => {
          const promoCodeAnalytics = getAppliedPromoCodeAnalyticsData(booking);
          if (promoCodeAnalytics) {
            trackEvent("PromoCodeRedeemed", promoCodeAnalytics);
          }
        });
        if (!isRequestBooking) {
          showToast({
            title: t`Bookings Confirmed`,
            semanticStyle: "success",
            toastId: "confirm-draft-bookings-success",
          });
        }
        exitMultiTeeTimeMode();
        if (successCallback) {
          successCallback();
        }
      },
    });

  return [handleConfirmDraftBookings, isConfirming] as const;
};

const useConfirmDraftBookingsMutationString = graphql`
  mutation useConfirmDraftBookingsMutation(
    $input: ConfirmDraftBookingsInput!
    $membershipTypeId: ID!
    $bookingType: BookingType!
  ) {
    responsePayload: confirmDraftBookings(input: $input) {
      bookings {
        id
        ...promoCodeAnalytics_booking
        ...buildBookingConfirmationAnalyticsData_booking
        ...ChargesSummary_booking @arguments(patronLoyalty: false)
        ...PaymentCardEntryModals_booking
        ...BookingPayment_Booking @arguments(patronLoyalty: false)
        ...InviteOthersBox_Booking
        ...BookingInfo_Booking
        ...RoundList_Booking
        ...BookingPolicy_Booking
        ...BookingPaymentMethodSelect_booking
        ...BookingReservation_Booking @arguments(patronLoyalty: false)
        ...BookingHeaderFragment_booking
        ...CancelBookingButton_Booking
        startSlot {
          id
          ...TeeSheetAgendaConsistency_timeSlot
        }
      }
      errors {
        field
        code
        message
      }
      success
    }
  }
`;
